import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { AgentProvider } from "@/contexts/AgentContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AgentAssistant } from "@/components/agent/AgentAssistant";
import { CommandPalette } from "@/components/CommandPalette";
import { InstallPrompt } from "@/components/pwa/InstallPrompt";
import { UpdatePrompt } from "@/components/pwa/UpdatePrompt";
import { ScrollToTop } from "@/components/ScrollToTop";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import WorkspaceSettings from "./pages/WorkspaceSettings";
import Base from "./pages/Base";
import Templates from "./pages/Templates";
import FormBuilder from "./pages/FormBuilder";
import AutomationBuilder from "./pages/AutomationBuilder";
import PublicForm from "./pages/PublicForm";
import Settings from "./pages/Settings";
import NotFound from "./pages/NotFound";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Docs from "./pages/Docs";
import Install from "./pages/Install";
import ResetPassword from "./pages/ResetPassword";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AuthProvider>
            <AgentProvider>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <ScrollToTop />
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<Index />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/install" element={<Install />} />
                <Route path="/form/submit/:shareToken" element={<PublicForm />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/docs" element={<Docs />} />
                
                {/* Protected routes */}
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } />
                <Route path="/settings" element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                } />
                <Route path="/workspace/:workspaceId/settings" element={
                  <ProtectedRoute>
                    <WorkspaceSettings />
                  </ProtectedRoute>
                } />
                {/* Redirect old workspace routes to dashboard */}
                <Route path="/workspace/:workspaceId" element={<Navigate to="/dashboard" replace />} />
                <Route path="/base/:baseId" element={
                  <ProtectedRoute>
                    <Base />
                  </ProtectedRoute>
                } />
                <Route path="/templates" element={
                  <ProtectedRoute>
                    <Templates />
                  </ProtectedRoute>
                } />
                <Route path="/form/:formId" element={
                  <ProtectedRoute>
                    <FormBuilder />
                  </ProtectedRoute>
                } />
                <Route path="/automation/:automationId" element={
                  <ProtectedRoute>
                    <AutomationBuilder />
                  </ProtectedRoute>
                } />
                
                {/* Catch-all route */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              <AgentAssistant />
              <CommandPalette />
              <InstallPrompt />
              <UpdatePrompt />
              </TooltipProvider>
            </AgentProvider>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
